import { useQuery } from '@tanstack/react-query';
import { checkFeatureFlags } from '@/lib/api/feature-flags';

/**
 * Returns the resolution status of a single feature flag for the current
 * workspace. Use this (over {@link useFeatureFlag}) when the consumer must
 * distinguish "still loading" from "resolved disabled" — e.g. to avoid
 * flashing a fallback UI before the flag resolves.
 *
 * staleTime is intentionally 30s (not 5min from the original plan):
 * 60s backend cache + 5min frontend = up to 6min toggle latency (ADR-015 P2).
 * 30s frontend reduces max latency to ~90s.
 *
 * @param name - Feature flag name (e.g. 'tool.amazon_title')
 * @returns `enabled` (`false` while loading/disabled) and `isPending`
 *          (`true` until the first check resolves).
 */
export function useFeatureFlagStatus(name: string): {
  enabled: boolean;
  isPending: boolean;
} {
  const { data, isPending } = useQuery({
    queryKey: ['feature-flags', 'check', name],
    queryFn: () => checkFeatureFlags([name]),
    staleTime: 30 * 1000,
  });
  return { enabled: data?.enabled.includes(name) ?? false, isPending };
}

/**
 * Returns whether a single feature flag is enabled for the current workspace.
 *
 * @param name - Feature flag name (e.g. 'tool.amazon_title')
 * @returns `true` when the flag is enabled, `false` while loading or when disabled.
 */
export function useFeatureFlag(name: string): boolean {
  return useFeatureFlagStatus(name).enabled;
}
